/**
 * Lizenz-Sektion (Seat-Lizenz, N2-2) für einen einzelnen Mandanten.
 * Wird als Sektion in TenantDetailPage eingehängt (Muster: BrandingSection).
 *
 * Eine Lizenz bindet eine externe Referenz (extRef) + Marke an ein
 * Seat-Limit. `seats === 0` = unbegrenzt. Sie wird — anders als der Vertrag —
 * erst durch ein explizites PUT angelegt; bis dahin meldet der Service
 * `provisioned:false` / Status `none` (= unbegrenzt, nie blockierend).
 *
 *   - PUT /license          → extRef/brand/seats setzen
 *   - PUT /license/status   → active|suspended
 * Der Seat-Delta-Endpoint (POST /license/seats) ist ein Enforcement-Hook für
 * das User-Provisioning und wird hier bewusst nur lesend dargestellt.
 */
import { useEffect, useState } from 'react';
import api, { LicenseView, extractApiError } from '../services/api';
import { labelForLicenseStatus } from '../services/status-labels';

interface Props {
  slug: string;
  isReadOnly: boolean;
}

const BRAND_RE = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/;

export function LicenseSection({ slug, isReadOnly }: Props) {
  const [loaded, setLoaded] = useState(false);
  const [license, setLicense] = useState<LicenseView | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [extRef, setExtRef] = useState('');
  const [brand, setBrand] = useState('sectepe');
  const [seats, setSeats] = useState('0');

  function applyLicense(l: LicenseView): void {
    setLicense(l);
    setExtRef(l.extRef ?? '');
    setBrand(l.brand ?? 'sectepe');
    setSeats(String(l.seats));
  }

  useEffect(() => {
    let cancelled = false;
    api.get<LicenseView>(`/tenants/${slug}/license`).then((r) => {
      if (cancelled) return;
      applyLicense(r.data);
      setLoaded(true);
    }).catch(() => {
      if (!cancelled) { setErr('Lizenz konnte nicht geladen werden'); setLoaded(true); }
    });
    return () => { cancelled = true; };
  }, [slug]);

  async function run(fn: () => Promise<LicenseView>): Promise<void> {
    if (isReadOnly) return;
    setBusy(true);
    setErr(null);
    try {
      applyLicense(await fn());
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const extRefValid = extRef.trim().length > 0 && extRef.length <= 128;
  const brandValid = BRAND_RE.test(brand);
  const seatsNum = Number(seats);
  const seatsValid = Number.isInteger(seatsNum) && seatsNum >= 0;

  const saveLicense = () => {
    if (!extRefValid || !brandValid || !seatsValid) {
      setErr('extRef (1–128 Zeichen), Marke (Slug) und Seats (≥ 0) erforderlich.');
      return;
    }
    void run(async () => (await api.put<LicenseView>(`/tenants/${slug}/license`, {
      extRef: extRef.trim(),
      brand,
      seats: seatsNum,
    })).data);
  };

  const setStatus = (status: 'active' | 'suspended') =>
    run(async () => (await api.put<LicenseView>(`/tenants/${slug}/license/status`, { status })).data);

  if (!loaded) return <p className="text-slate-500 text-sm">Lade Lizenz…</p>;

  const l = license;
  const remaining = l?.seatsRemaining === null ? 'unbegrenzt' : String(l?.seatsRemaining ?? 0);

  return (
    <section className="mb-6">
      <h3 className="font-semibold mb-2">Lizenz (Seats)</h3>
      <p className="text-xs text-slate-500 mb-3">
        Bindet eine externe Referenz an ein Seat-Limit für diesen Mandanten.
        <span className="font-mono"> Seats = 0</span> bedeutet unbegrenzt; ohne
        gesetzte Lizenz werden Seat-Checks nicht blockiert.
      </p>

      {err && <div className="bg-red-100 text-red-700 p-3 rounded mb-3 text-sm">{err}</div>}
      {l && !l.provisioned && (
        <div className="bg-amber-50 text-amber-800 p-3 rounded mb-3 text-sm">
          Noch keine Lizenz gesetzt (unbegrenzt). Felder ausfüllen und speichern, um eine zu binden.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded shadow p-4 space-y-3">
          <div>
            <label className="block text-sm text-slate-600 mb-1">Externe Referenz (extRef)</label>
            <input
              value={extRef}
              onChange={(e) => setExtRef(e.target.value)}
              disabled={isReadOnly}
              maxLength={128}
              placeholder="z.B. Commerce-Order-ID"
              className="w-full px-2 py-1 rounded border text-sm"
            />
            {!extRefValid && extRef.length > 0 && (
              <p className="text-xs text-red-600 mt-1">1 bis 128 Zeichen erforderlich.</p>
            )}
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Marke</label>
            <input
              value={brand}
              onChange={(e) => setBrand(e.target.value.toLowerCase())}
              disabled={isReadOnly}
              placeholder="sectepe"
              className="w-full px-2 py-1 rounded border font-mono text-sm"
            />
            {!brandValid && (
              <p className="text-xs text-red-600 mt-1">Kleinbuchstaben-Slug erwartet (a-z0-9-, 2–32 Zeichen).</p>
            )}
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Seats (0 = unbegrenzt)</label>
            <input
              type="number" min={0} step="1"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              disabled={isReadOnly}
              className="w-full px-2 py-1 rounded border font-mono text-sm"
            />
            {!seatsValid && (
              <p className="text-xs text-red-600 mt-1">Nicht-negative Ganzzahl erforderlich.</p>
            )}
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={saveLicense}
              disabled={busy || isReadOnly || !extRefValid || !brandValid || !seatsValid}
              className="btn-primary disabled:opacity-50"
            >
              Speichern
            </button>
            {l?.provisioned && l.status === 'active' && (
              <button onClick={() => setStatus('suspended')} disabled={busy || isReadOnly} className="btn-danger">
                Aussetzen
              </button>
            )}
            {l?.provisioned && l.status === 'suspended' && (
              <button onClick={() => setStatus('active')} disabled={busy || isReadOnly} className="btn-primary">
                Aktivieren
              </button>
            )}
          </div>
          {isReadOnly && (
            <p className="text-xs text-slate-400">Read-only-Operator; Lizenz kann nicht bearbeitet werden.</p>
          )}
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-600">Seat-Auslastung</h4>
          <div className="bg-white rounded shadow p-4 text-sm space-y-1">
            <div>Status: <span className="badge bg-slate-200">{labelForLicenseStatus(l?.status ?? 'none')}</span></div>
            <div>Lizenziert: <span className="font-mono">{l?.seats === 0 ? 'unbegrenzt' : l?.seats}</span></div>
            <div>Belegt: <span className="font-mono">{l?.usedSeats ?? 0}</span></div>
            <div>Frei: <span className="font-mono">{remaining}</span></div>
          </div>
          <p className="text-xs text-slate-400">
            „Belegt" wird vom User-Provisioning fortgeschrieben; hier nur lesend.
          </p>
        </div>
      </div>
    </section>
  );
}
